import { readFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import { parseSkillMd } from "./skill-manifest.js";

interface SkillManifestSla {
  p95_latency_ms?: number;
  max_error_rate?: number; // 0.0-1.0
  min_uptime_pct?: number; // 0.0-100.0
  max_avg_tokens?: number;
}

interface SkillManifestEntry {
  name: string;
  version?: string;
  description?: string;
  sla?: SkillManifestSla;
  // New fields for SKILL.md support
  skill_content_path?: string;   // Path to SKILL.md file
  mcp_metadata?: {
    tools?: Array<{ name: string; description?: string; input_schema?: Record<string, unknown> }>;
    model_preferences?: Record<string, unknown>;
    resources?: Array<Record<string, unknown>>;
  };
  tags?: string[];
  changelog?: string;
  schema_definition?: Record<string, unknown>;
  instructions?: string;
}

interface SkillManifest {
  skills: SkillManifestEntry[];
}

interface PublishOpts {
  manifest?: string;
  apiKey?: string;
  apiUrl: string;
}

export async function publishSkills(opts: PublishOpts): Promise<void> {
  if (!opts.manifest) {
    console.error("Error: --manifest is required");
    process.exit(1);
  }
  if (!opts.apiKey) {
    console.error("Error: --api-key or AGENTVAULT_API_KEY is required");
    process.exit(1);
  }

  // Read and parse manifest
  const manifestPath = resolve(opts.manifest);
  let raw: string;
  try {
    raw = readFileSync(manifestPath, "utf-8");
  } catch {
    console.error(`Error: Cannot read manifest file: ${manifestPath}`);
    process.exit(1);
  }

  let manifest: SkillManifest;
  try {
    manifest = JSON.parse(raw);
  } catch {
    console.error("Error: Invalid JSON in manifest file");
    process.exit(1);
  }

  // Validate manifest structure
  if (!Array.isArray(manifest.skills) || manifest.skills.length === 0) {
    console.error("Error: Manifest must contain a non-empty 'skills' array");
    process.exit(1);
  }
  for (const skill of manifest.skills) {
    if (!skill.name || typeof skill.name !== "string") {
      console.error("Error: Each skill must have a 'name' string");
      process.exit(1);
    }
    if (
      skill.sla?.max_error_rate !== undefined &&
      (skill.sla.max_error_rate < 0 || skill.sla.max_error_rate > 1)
    ) {
      console.error(
        `Error: Skill "${skill.name}" max_error_rate must be 0.0-1.0`,
      );
      process.exit(1);
    }
    if (
      skill.sla?.min_uptime_pct !== undefined &&
      (skill.sla.min_uptime_pct < 0 || skill.sla.min_uptime_pct > 100)
    ) {
      console.error(
        `Error: Skill "${skill.name}" min_uptime_pct must be 0-100`,
      );
      process.exit(1);
    }
  }

  // Transform to API format
  const capabilities = manifest.skills.map((s) => {
    // Load SKILL.md content if path specified
    let skillContent: string | null = null;
    let mcpMeta = s.mcp_metadata ?? null;
    let skillTags = s.tags ?? null;
    let skillInstructions = s.instructions ?? null;
    let schemaDef = s.schema_definition ?? null;

    if (s.skill_content_path) {
      const contentPath = resolve(s.skill_content_path);
      if (existsSync(contentPath)) {
        skillContent = readFileSync(contentPath, "utf-8");
        // Try to parse SKILL.md for additional metadata
        const parsed = parseSkillMd(skillContent);
        if (parsed) {
          // SKILL.md metadata fills in missing manifest fields
          if (!s.description && parsed.description) s.description = parsed.description;
          if (!s.version && parsed.version) s.version = parsed.version;
          if (!skillTags && parsed.tags) skillTags = parsed.tags;
          if (!skillInstructions && parsed.instructions) skillInstructions = parsed.instructions;
          if (!schemaDef && parsed.inputSchema) schemaDef = parsed.inputSchema as Record<string, unknown>;
          if (!s.sla && parsed.slaDefinition) {
            s.sla = parsed.slaDefinition as SkillManifestSla;
          }
        }
      } else {
        console.warn(`Warning: SKILL.md not found at ${contentPath}, skipping content`);
      }
    }

    return {
      capability_name: s.name,
      capability_version: s.version ?? null,
      description: s.description ?? null,
      sla_definition: s.sla
        ? {
            p95_latency_ms: s.sla.p95_latency_ms ?? null,
            max_error_rate: s.sla.max_error_rate ?? null,
            min_uptime_pct: s.sla.min_uptime_pct ?? null,
            max_avg_tokens: s.sla.max_avg_tokens ?? null,
          }
        : null,
      // New fields
      skill_content: skillContent,
      mcp_metadata: mcpMeta,
      tags: skillTags,
      changelog: s.changelog ?? null,
      schema_definition: schemaDef,
      instructions: skillInstructions,
    };
  });

  // POST to backend
  const url = `${opts.apiUrl}/api/v1/hub/skills/publish`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${opts.apiKey}`,
    },
    body: JSON.stringify(capabilities),
  });

  if (!res.ok) {
    const text = await res.text();
    console.error(`Error: API returned ${res.status}: ${text}`);
    process.exit(1);
  }

  const result = (await res.json()) as Array<{
    capability_name: string;
    capability_version?: string;
    verification_status?: string;
    sla_definition?: object;
    mcp_metadata?: object;
    tags?: string[];
  }>;
  console.log(`Published ${result.length} skill(s) successfully.`);
  for (const cap of result) {
    const status = cap.verification_status ?? "declared";
    const sla = cap.sla_definition ? " (SLA)" : "";
    const mcp = cap.mcp_metadata ? " (MCP)" : "";
    const tagStr = cap.tags?.length ? ` [${cap.tags.join(", ")}]` : "";
    console.log(
      `  - ${cap.capability_name}${cap.capability_version ? `@${cap.capability_version}` : ""}: ${status}${sla}${mcp}${tagStr}`,
    );
  }
}
